import { useLayoutEffect, useRef } from 'react'
import type { OrderData } from '../../bot/components/GraphCandle'

// Data colors matching the Backtest tab chart's markers/tooltip exactly (they
// are theme-invariant there too, and the canvas arrows use the same hexes —
// keeping the card and the markers in lockstep).
const OPEN_GREY = '#9E9E9E'
const PNL_GAIN = '#00C853'
const PNL_LOSS = '#FF1744'

export interface WorkbenchBtOrderCardProps {
  /** Orders on the clicked marker's candle, split into Opened/Closed below. */
  orders: OrderData[]
  /** Marker anchor in chart-wrap pixel space. */
  x: number
  y: number
}

/**
 * Click card for backtest order markers — the same information the Backtest
 * tab chart shows in its click tooltip (GraphCandle subscribeClick): an
 * Opened section with entry/SL/TP and a Closed section with PnL-colored
 * entry→exit, reason and volume.
 */
export function WorkbenchBtOrderCard({ orders, x, y }: WorkbenchBtOrderCardProps) {
  const ref = useRef<HTMLDivElement>(null)

  // Same flip-at-the-right-edge placement as the backtest tooltip. Applied
  // straight to the DOM after measuring, so no second render is needed.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const wrapWidth = el.parentElement?.clientWidth ?? 0
    let left = x + 16
    if (left + el.offsetWidth > wrapWidth) left = x - el.offsetWidth - 10
    el.style.left = `${Math.max(0, left)}px`
    el.style.top = `${Math.max(0, y - 20)}px`
  })

  const opened = orders.filter((o) => o.eventType === 'opened')
  const closed = orders.filter((o) => o.eventType === 'closed')

  return (
    <div
      ref={ref}
      data-testid="bt-order-card"
      className="pointer-events-none absolute z-10 max-w-[280px] rounded-md border border-border bg-popover px-2.5 py-2 text-label leading-normal text-foreground shadow-popover"
    >
      <div className="mb-1 border-b border-border pb-1 font-semibold">
        Orders ({orders.length})
      </div>

      {opened.length > 0 && (
        <>
          <div className="mb-0.5 text-caption font-semibold opacity-60">Opened</div>
          {opened.map((o) => (
            <div key={String(o.orderId)} className="mb-1">
              <span className="font-semibold" style={{ color: OPEN_GREY }}>
                {o.type === 'BUY' ? '▲' : '▼'} {o.type}
              </span>
              {o.volume != null && (
                <span className="ml-1 text-caption opacity-50">{o.volume.toFixed(2)} lot</span>
              )}
              <div className="mt-px flex gap-2.5 text-caption opacity-85">
                <span>Entry: {o.price.toFixed(5)}</span>
                <span>SL: {o.sl.toFixed(5)}</span>
                <span>TP: {o.tp.toFixed(5)}</span>
              </div>
            </div>
          ))}
        </>
      )}

      {closed.length > 0 && (
        <>
          {opened.length > 0 && <div className="my-1 border-t border-border" />}
          <div className="mb-0.5 text-caption font-semibold opacity-60">Closed</div>
          {closed.map((o) => {
            const pnl = o.pnl ?? 0
            const pnlColor = pnl > 0 ? PNL_GAIN : PNL_LOSS
            return (
              <div key={String(o.orderId)} className="mb-1">
                <span className="font-semibold" style={{ color: pnlColor }}>
                  {o.type === 'BUY' ? '▲' : '▼'} {o.type}
                </span>
                <span className="ml-1.5 font-semibold" style={{ color: pnlColor }}>
                  {pnl > 0 ? '+' : ''}
                  {pnl.toFixed(2)}
                </span>
                {o.volume != null && (
                  <span className="ml-1 text-caption opacity-50">{o.volume.toFixed(2)} lot</span>
                )}
                <div className="mt-px flex gap-2.5 text-caption opacity-85">
                  <span>
                    {(o.entryPrice ?? o.price).toFixed(5)} {'→'}{' '}
                    {(o.exitPrice ?? o.price).toFixed(5)}
                  </span>
                  {o.exitReason && <span>{o.exitReason}</span>}
                </div>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
